import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import axios from "axios";

import { setStorageScope, setStorageWriteHook } from "@/lib/storageScope";
import {
    bootstrapCloudSync,
    teardownCloudSync,
    notifyWrite as cloudSyncNotifyWrite,
} from "@/lib/cloudSync";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Every auth call uses cookies — make sure axios includes them by default.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

/**
 * Map FastAPI error payloads to a flat string so React renders cleanly.
 * (FastAPI 422 returns `[{msg, type, loc}]` arrays which crash JSX if you
 * setState them directly.)
 */
export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function AuthProvider({ children }) {
    // null = checking; user object = authenticated; false = anonymous
    const [user, setUser] = useState(null);
    // 'idle' before login, 'syncing' while bootstrapCloudSync runs, 'ready' once done.
    // Stays 'idle' for anonymous users.
    const [cloudSyncState, setCloudSyncState] = useState("idle");
    const cloudSyncedUserRef = useRef(null);

    // Apply the storage scope synchronously whenever the user changes so
    // ToneGuardPage's useEffect reads land in the right namespace.
    const applyUser = useCallback((next) => {
        setUser(next);
        const nextUserId = next && next.user_id ? next.user_id : null;
        setStorageScope(nextUserId);
        // User changed → cancel any in-flight cloud sync for the previous
        // identity. Bootstrap for the new user is triggered by the effect
        // below so we always reconcile with server state before mounting
        // protected components.
        if (!nextUserId) {
            teardownCloudSync();
            setStorageWriteHook(null);
            setCloudSyncState("idle");
            cloudSyncedUserRef.current = null;
        } else if (cloudSyncedUserRef.current !== nextUserId) {
            // Reset to 'syncing' so AuthGate can show a splash. The bootstrap
            // effect picks it up on the next render.
            setCloudSyncState("syncing");
        }
    }, []);

    // Run cloud bootstrap whenever the resolved user_id changes. Mounting
    // protected components waits for this to flip back to 'ready' so the
    // hydrated localStorage is in place before children read from it.
    useEffect(() => {
        const userId = user && user.user_id;
        if (!userId) return;
        if (cloudSyncedUserRef.current === userId) return;
        let cancelled = false;
        (async () => {
            await bootstrapCloudSync(userId);
            if (cancelled) return;
            // Wire the write hook AFTER bootstrap so the hydration writes
            // themselves don't trigger a redundant push round-trip.
            setStorageWriteHook(cloudSyncNotifyWrite);
            cloudSyncedUserRef.current = userId;
            setCloudSyncState("ready");
        })();
        return () => {
            cancelled = true;
        };
    }, [user]);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`);
            applyUser(data);
        } catch (_) {
            applyUser(false);
        }
    }, [applyUser]);

    useEffect(() => {
        // If we're processing an Emergent OAuth callback in the URL hash,
        // skip the /me probe — AuthCallback will exchange the session_id
        // and re-trigger checkAuth.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(
        async (email, password) => {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            applyUser(data);
            return data;
        },
        [applyUser],
    );

    const signup = useCallback(
        async (email, password, confirmPassword) => {
            const { data } = await axios.post(`${API}/auth/register`, {
                email,
                password,
                confirm_password: confirmPassword,
            });
            applyUser(data);
            return data;
        },
        [applyUser],
    );

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`);
        } catch (_) {
            /* ignore — clear local state regardless */
        }
        applyUser(false);
    }, [applyUser]);

    const forgotPassword = useCallback(async (email) => {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        return data; // { ok, reset_token, reset_link, expires_at }
    }, []);

    const resetPassword = useCallback(
        async (token, password, confirmPassword) => {
            const { data } = await axios.post(`${API}/auth/reset-password`, {
                token,
                password,
                confirm_password: confirmPassword,
            });
            applyUser(data);
            return data;
        },
        [applyUser],
    );

    const startGoogleLogin = useCallback(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
            redirectUrl,
        )}`;
    }, []);

    const exchangeGoogleSession = useCallback(
        async (sessionId) => {
            const { data } = await axios.post(`${API}/auth/google/session`, {
                session_id: sessionId,
            });
            applyUser(data);
            return data;
        },
        [applyUser],
    );

    const value = useMemo(
        () => ({
            user,
            loading: user === null,
            authenticated: !!(user && user.user_id),
            cloudSyncState,
            login,
            signup,
            logout,
            forgotPassword,
            resetPassword,
            startGoogleLogin,
            exchangeGoogleSession,
            refresh: checkAuth,
        }),
        [
            user,
            cloudSyncState,
            login,
            signup,
            logout,
            forgotPassword,
            resetPassword,
            startGoogleLogin,
            exchangeGoogleSession,
            checkAuth,
        ],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
